import React, { useEffect, useState } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import api from '../../../services/api';

import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, Controller } from 'react-hook-form';
import { FiHome } from 'react-icons/fi';
import { Book, YoutubeLogo } from 'phosphor-react';
import toast from 'react-hot-toast';

import Breadcrumbs from '../../../components/Breadcrumbs';
import LoaderBIO from '../../../components/Loader';
import Input from '../../../components/Form/Input';
import InputMask from '../../../components/Form/InputMask';
import SimpleMultiSelect from '../../../components/Form/SimpleMultiSelect';
import MessageErrorValidator from '../../../components/MessageErrorValidator';

import {
  Container,
  FakeDropZone,
  HeaderSection,
  Line1,
  Line2,
  Line3,
  Line4,
  LineButton,
  SectionContainer1,
  Separator,
} from './styles';
import { IClusterProps } from '../../../interfaces/Cluster';
import { IGenericOptions } from '../../../interfaces/IGenericOptions';
import Button from '../../../components/Button';
import Textarea from '../../../components/Form/Textarea';
import DropZone from '../../../components/Form/DropZone';
import CardMaterial from '../../../components/CardMaterial';

import { loadConfettiAllPage } from '../../../utils/useConfetti';
import { fileExtensionType } from '../../../utils/fileExtensionType';

import { ILessonProps } from '../../../interfaces/Lesson';
import { IAttachmentsProps } from '../../../interfaces/Attachments';
import { handleVideoThumbinalDependingProvider } from '../../../utils/videoThumbinalDependingProvider';
import { IProviders } from '../../../interfaces/Providers';

const AulaEdit: React.FC = () => {
  const { clusterSlug, id } = useParams<{ clusterSlug: string; id: string }>();
  const history = useHistory();

  // Validation
  const schema = Joi.object({
    cluster_slug: Joi.string()
      .required()
      .messages({ '*': 'Informe um módulo válido.' }),
    title: Joi.string().min(10).max(100).required().messages({
      '*': 'Informe um título válido.',
      'string.min': `O campo título deve ter no mínimo {#limit} caracteres`,
      'string.max': `O campo título deve ter no máximo {#limit} caracteres`,
    }),
    duration: Joi.string()
      .min(8)
      .max(8)
      .required()
      .messages({ '*': 'Informe uma duração válida.' }),
    video_provider: Joi.string()
      .required()
      .messages({ '*': 'Informe um provedor de vídeos válido.' }),
    video: Joi.string()
      .min(7)
      .required()
      .messages({ '*': 'Informe um código válido.' }),
    description: Joi.string().allow('').messages({
      '*': 'Informe uma descrição válida.',
      'string.min': `O campo descrição deve ter no mínimo {#limit} caracteres`,
      'string.max': `O campo descrição deve ter no máximo {#limit} caracteres`,
    }),
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: joiResolver(schema),
  });

  // States
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentProvider, setCurrentProvider] = useState<IProviders>('panda');

  // Data
  const [currentLesson, setCurrentLesson] = useState<ILessonProps>(
    {} as ILessonProps,
  );
  const [clustersData, setClustersData] = useState<IGenericOptions[]>([]);
  const [filesData, setFilesData] = useState<File[]>([]);

  // Loaders
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get(
          `/clusters/${clusterSlug}/lessons/${id}`,
        );

        if (!clusterSlug || !id) return;

        console.log('Complete lesson::: ', response.data);
        setCurrentLesson(response.data);
        setFilesData(response.data.files || []);

        const {
          cluster_slug,
          title,
          duration,
          video_provider,
          video,
          description,
        } = response.data;

        setCurrentVideoUrl(video);
        setCurrentProvider(video_provider);

        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration - hours * 3600) / 60);
        const seconds = duration - hours * 3600 - minutes * 60;

        reset({
          cluster_slug,
          title,
          video_provider,
          video,
          description,
          duration: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        });
      } catch (error) {
        console.log('error: ', error);
      }
    })();
  }, [clusterSlug, id, setValue, reset]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const response = await api.get(`/clusters`, {
          params: {
            pageIndex: 0,
            pageSize: 999,
            search: '',
          },
        });

        const clusters = response.data?.records;

        const options: IGenericOptions[] = clusters.map(
          (cluster: IClusterProps) => {
            return {
              value: cluster.slug,
              label: cluster.title,
            };
          },
        );

        setClustersData(options);
      } catch (error) {
        console.log(error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSubmit = handleSubmit(async data => {
    try {
      setLoadingSubmit(true);

      const [hours, minutes, seconds] = data.duration.split(':');
      const durationInNumber =
        Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);

      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('duration', String(durationInNumber));
      formData.append('video_provider', data.video_provider);
      formData.append('video', data.video);
      formData.append('audio', '');
      formData.append('description', data.description || '');

      let index = 0;

      if (currentLesson?.attachments?.length > 0) {
        currentLesson.attachments.forEach(attachment => {
          formData.append(`attachments[${index}][id]`, attachment.id);
          formData.append(
            `attachments[${index}][lesson_id]`,
            attachment.lesson_id,
          );
          formData.append(`attachments[${index}][name]`, attachment.name);
          formData.append(`attachments[${index}][type]`, attachment.type);
          index++;
        });
      }

      if (filesData.length > 0) {
        filesData.forEach(file => {
          formData.append(`attachments[${index}][name]`, String(file.name));
          formData.append(`attachments[${index}][file]`, file);

          const fileExtension = file.name.split('.').pop();
          if (!fileExtension) return;
          formData.append(
            `attachments[${index}][type]`,
            String(fileExtensionType(fileExtension)),
          );

          index++;
        });
      }

      const response = await api.put(
        `/clusters/${data.cluster_slug}/lessons/${id}`,
        formData,
      );
      console.log('response', response.data);

      if (response.status === 200) {
        toast.success('Aula editada com sucesso!', {
          position: 'bottom-right',
          duration: 5000,
          className: 'toast-samuquinha',
        });

        loadConfettiAllPage();

        history.push(`/aulas/${data.cluster_slug}`);
      }
    } catch (error: any) {
      console.log(error);

      if (error.response.data.error === 'lesson-already-exists') {
        toast.error('Ops! Esta aula já foi cadastrada na plataforma! ', {
          position: 'bottom-right',
          duration: 5000,
          className: 'toast-samuquinha',
        });

        return;
      }

      toast.error(
        'Erro ao editar a aula! Por favor, entre em contato com o suporte técnico.',
        {
          position: 'bottom-right',
          duration: 5000,
          className: 'toast-samuquinha',
        },
      );
    } finally {
      setLoadingSubmit(false);
    }
  });

  return (
    <>
      <LoaderBIO isVisible={loading} />

      <Container>
        <HeaderSection>
          <Breadcrumbs
            icon={FiHome}
            separator="/"
            route={[
              { title: 'Módulos', link: '/modulos' },
              {
                title: `${currentLesson?.cluster?.title}`,
                link: `/aulas/${clusterSlug}`,
              },
              {
                title: `${currentLesson.title}`,
                link: `/aula/editar/${clusterSlug}/${id}`,
              },
            ]}
          />
          <aside>{/* button? */}</aside>
        </HeaderSection>

        <SectionContainer1>
          <form onSubmit={onSubmit}>
            <Separator>
              <YoutubeLogo size={20} weight="bold" />
              <h2>Informe os dados da aula abaixo:</h2>
            </Separator>
            <Line1>
              <div>
                <SimpleMultiSelect
                  register={register}
                  name="cluster_slug"
                  label="Módulo"
                  placeHolder="Selecione..."
                  options={clustersData}
                  setResult={values => {
                    setValue('cluster_slug', values);
                  }}
                  isMulti={false}
                  defaultValue={clusterSlug}
                  required
                />
                <MessageErrorValidator>
                  {errors?.cluster_slug?.message as string}
                </MessageErrorValidator>
              </div>
              <div>
                <Input
                  register={register}
                  name="title"
                  type="text"
                  label="Título da aula"
                  autoComplete="off"
                />
                <MessageErrorValidator>
                  {errors?.title?.message as string}
                </MessageErrorValidator>
              </div>
              <div>
                <InputMask
                  register={register}
                  name="duration"
                  type="text"
                  label="duração da aula"
                  pattern="##:##:##"
                  autoComplete="off"
                  setResult={values => {
                    setValue('duration', values);
                  }}
                />
                <MessageErrorValidator>
                  {errors?.duration?.message as string}
                </MessageErrorValidator>
              </div>
            </Line1>
            <Line2>
              <div>
                <Controller
                  name="video_provider"
                  control={control}
                  render={({ field }) => (
                    <SimpleMultiSelect
                      register={register}
                      name={field.name}
                      label="Provedor do vídeo"
                      placeHolder="Selecione..."
                      options={[
                        { value: 'panda', label: 'Panda' },
                        { value: 'vimeo', label: 'Vimeo' },
                      ]}
                      setResult={values => {
                        setValue('video_provider', values);
                        setCurrentProvider(values);
                      }}
                      defaultValue={field.value}
                      isMulti={false}
                      required
                    />
                  )}
                />
                <MessageErrorValidator>
                  {errors?.video_provider?.message as string}
                </MessageErrorValidator>
              </div>
              <div>
                <Input
                  register={register}
                  name="video"
                  type="text"
                  label="Código do vídeo"
                  autoComplete="off"
                  maxLength={100}
                  placeholder="Ex: 123456789"
                  onChange={e => setCurrentVideoUrl(e.target.value)}
                />
                <MessageErrorValidator>
                  {errors?.video?.message as string}
                </MessageErrorValidator>
              </div>
            </Line2>
            {currentVideoUrl.length > 6 && (
              <Line3>
                <div>
                  <img
                    src={handleVideoThumbinalDependingProvider(
                      currentProvider,
                      currentVideoUrl,
                    )}
                    alt=""
                  />
                </div>
                <div>
                  <Textarea
                    register={register}
                    name="description"
                    label="Descrição da aula"
                    autoComplete="off"
                    type="text"
                    rows={10}
                  />
                  <MessageErrorValidator>
                    {errors?.description?.message as string}
                  </MessageErrorValidator>
                </div>
              </Line3>
            )}
            <Separator>
              <Book size={20} weight="bold" />
              <h2>Adicione novos materiais a aula no campo abaixo:</h2>
            </Separator>
            <Line4>
              <div>
                <DropZone
                  setFilesData={setFilesData}
                  maxSize={1024 * 1024 * 3} // 3 mb
                  isMulti={true}
                  icons={true}
                  accept={{
                    'image/png': ['.png', '.jpg', '.jpeg', '.webp'],
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc', '.docx'],
                  }}
                />
              </div>
              <Separator style={{ marginTop: '1rem' }}>
                <Book size={20} weight="bold" />
                <h2>Materiais da aula:</h2>
              </Separator>
              <div>
                {currentLesson?.attachments?.length > 0 ? (
                  <FakeDropZone>
                    {currentLesson?.attachments?.map(
                      (file: IAttachmentsProps, index) => (
                        <CardMaterial
                          key={index}
                          href={file.file_url}
                          title={file.name}
                          type={file.type}
                          onDelete={() => {
                            if (
                              window.confirm(
                                'Deseja realmente excluir este material?',
                              )
                            ) {
                              setCurrentLesson({
                                ...currentLesson,
                                attachments: currentLesson.attachments.filter(
                                  attachment => attachment.id !== file.id,
                                ),
                              });
                            }
                          }}
                        />
                      ),
                    )}
                  </FakeDropZone>
                ) : (
                  <p>Nenhum material encontrado para esta aula.</p>
                )}
              </div>
            </Line4>
            <LineButton>
              <Button type="submit" loading={loadingSubmit} color="primary">
                Editar Aula
              </Button>
            </LineButton>
          </form>
        </SectionContainer1>
      </Container>
    </>
  );
};

export default AulaEdit;
